import React from 'react'
import { Box, Card, CardContent, CardMedia, Skeleton, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'

interface ModernCardSkeletonProps {
  count?: number
  showActions?: boolean
  showStats?: boolean
}

// Simple skeleton card
const SkeletonCard = styled(Card)(() => ({
  position: 'relative',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
}))

// Simple skeleton with proper contrast
const SimpleSkeleton = styled(Skeleton)(() => ({
  backgroundColor: '#f3f4f6',
}))

// Simple skeleton media
const SkeletonMedia = styled(CardMedia)(() => ({
  height: 180,
  backgroundColor: '#f9fafb',
}))

// Simple content skeleton
const SimpleCardContent = styled(CardContent)(() => ({
  padding: '16px',
}))

// Single skeleton card component
const SingleCardSkeleton: React.FC<{
  showActions?: boolean
  showStats?: boolean
}> = ({ showActions = false, showStats = false }) => {
  return (
    <SkeletonCard>
      <SkeletonMedia>
        <SimpleSkeleton variant='rectangular' height={180} />
      </SkeletonMedia>

      <SimpleCardContent>
        {/* Title */}
        <SimpleSkeleton
          variant='rectangular'
          height={20}
          sx={{ marginBottom: '12px', width: '80%' }}
        />

        {/* User info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <SimpleSkeleton variant='circular' width={24} height={24} />
          <SimpleSkeleton variant='rectangular' height={14} sx={{ width: '50%' }} />
        </Box>

        {/* Date */}
        <SimpleSkeleton variant='rectangular' height={12} sx={{ width: '30%' }} />
      </SimpleCardContent>
    </SkeletonCard>
  )
}

// Main skeleton grid component
const ModernCardSkeleton: React.FC<ModernCardSkeletonProps> = ({
  count = 12,
  showActions = false,
  showStats = false,
}) => {
  const skeletonCards = Array.from({ length: count }, (_, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
      <SingleCardSkeleton showActions={showActions} showStats={showStats} />
    </Grid>
  ))

  return (
    <Grid container spacing={3}>
      {skeletonCards}
    </Grid>
  )
}

export default ModernCardSkeleton
